import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  limit
} from "firebase/firestore";
import Sidebar from "../components/Sidebar";
import Layout from "../components/Layout";
import * as XLSX from "xlsx";
import { fetchOnlineCollectionMap, fetchParties, fetchPreviousDayLedger, syncPartyMasterOpeningBalance } from "../services/cashierService";

function SalesmanLedgerManager() {
  const [ledgers, setLedgers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const [dateFilter, setDateFilter] = useState(todayStr);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadLedgers();
  }, []);

  const loadLedgers = async () => {
    setLoading(true);
    try {
      if (!dateFilter) {
        setLedgers([]);
        setLoading(false);
        return;
      }

      // 1. Fetch Hawker Party List (to identify valid IDs for filtering)
      const hawkerList = await fetchParties("all");
      const hawkerIds = new Set(hawkerList.map(h => (h.partyId || h.id).trim()));

      // 2. Fetch Salesman Ledger Records
      const q = query(
        collection(db, "salesman_ledger"),
        where("dateIST", "==", dateFilter),
        orderBy("partyId")
      );
      const snap = await getDocs(q);
      const ledgerList = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(l => hawkerIds.has((l.partyId || "").trim()));

      // 2. Fetch ALL Hawker CDM records for this date to ensure fresh display
      const [y, m, d] = dateFilter.split("-");
      const formattedDate = `${d}-${m}-${y}`;
      
      const cdmSnap = await getDocs(
        query(collection(db, "hawker_cdm"), where("date", "==", formattedDate))
      );
      
      const cdmMap = {};
      cdmSnap.forEach(doc => {
        const data = doc.data();
        cdmMap[data.partyId] = {
          cdm: Number(data.cdm || 0),
          niche: Number(data.nicheBrands || 0)
        };
      });

      // 4. Fetch Online Collection Map (Live from Sheets)
      const onlineMap = await fetchOnlineCollectionMap(dateFilter);

      // 5. Build Final List: One row for EVERY Hawker
      const fullList = await Promise.all(hawkerList.map(async (h) => {
        const partyId = (h.partyId || h.id).trim();
        const existing = ledgerList.find(l => (l.partyId || "").trim() === partyId);
        
        const liveCdm = cdmMap[partyId] || { cdm: 0, niche: 0 };
        const liveOnline = Number(onlineMap[partyId] || 0);

        if (existing) {
          return {
            ...existing,
            payment_mode: h.payment_mode || "Cash",
            cdmAmount: liveCdm.cdm || existing.cdmAmount || 0,
            nicheBrandAmount: liveCdm.niche || existing.nicheBrandAmount || 0,
            onlineCollected: existing.onlineCollected || liveOnline
          };
        } else {
          // No record in salesman_ledger yet. Fetch Opening Balance (from yesterday)
          const prev = await fetchPreviousDayLedger(partyId, dateFilter);
          const open = prev?.closingBalance || 0;
          
          return {
            id: `MOCK_${partyId}`, // Temp ID for display
            partyId: partyId,
            payment_mode: h.payment_mode || "Cash",
            dateIST: dateFilter,
            openingBalance: open,
            orderAmount: 0,
            cdmAmount: liveCdm.cdm,
            nicheBrandAmount: liveCdm.niche,
            onlineCollected: liveOnline,
            cashCollected: 0,
            closingBalance: open + liveCdm.cdm + liveCdm.niche - liveOnline,
            status: "MISSING", // Distinguish from PENDING
            dayClose: false,
            mock: true
          };
        }
      }));

      // Sort by Party ID
      fullList.sort((a,b) => (a.partyId || "").localeCompare(b.partyId || ""));
      setLedgers(fullList);
    } catch (err) {
      console.error(err);
      alert("Error loading ledgers");
    }
    setLoading(false);
  };

  const handleSearch = () => {
    let results = [...ledgers];
    if (searchTerm) {
      results = results.filter(
        (l) =>
          (l.partyId || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (l.id || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (dateFilter) {
      results = results.filter((l) => l.dateIST === dateFilter);
    }
    setFiltered(results);
  };

  useEffect(() => {
    handleSearch();
  }, [searchTerm, dateFilter, ledgers]);

  const updateFieldLocal = (id, field, value) => {
    setLedgers((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l))
    );
  };

  const saveLedgerChanges = async (ledger) => {
    try {
      // Recalculate closing balance before saving
      const open = Number(ledger.openingBalance || 0);
      const order = Number(ledger.orderAmount || 0);
      const cdm = Number(ledger.cdmAmount || 0);
      const niche = Number(ledger.nicheBrandAmount || 0);
      const online = Number(ledger.onlineCollected || 0);
      const cash = Number(ledger.cashCollected || 0);
      const closing = open + order + cdm + niche - online - cash;

      const updatedData = {
        openingBalance: open,
        orderAmount: order,
        cdmAmount: cdm,
        nicheBrandAmount: niche,
        onlineCollected: online,
        cashCollected: cash,
        closingBalance: closing,
        status: ledger.status || "PENDING",
        dayClose: !!ledger.dayClose,
        rejectionRemark: ledger.rejectionRemark || ""
      };

      await updateDoc(doc(db, "salesman_ledger", ledger.id), updatedData);

      // 2. Update local state instead of calling loadLedgers()
      setLedgers((prev) =>
        prev.map((l) => (l.id === ledger.id ? { ...l, ...updatedData } : l))
      );

      // ✅ SYNC TO PARTY MASTER if this is today's ledger
      if (ledger.dateIST === todayStr) {
        await syncPartyMasterOpeningBalance(ledger.partyId, open);
      }

      alert("Updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Error updating ledger");
    }
  };

  const handleExportExcel = () => {
    if (filtered.length === 0) {
      alert("No data to export.");
      return;
    }

    const dataToExport = filtered.map((l) => ({
      "Date": l.dateIST || "",
      "Party ID": l.partyId || "",
      "Mode": l.payment_mode || "Cash",
      "Opening": l.openingBalance || 0,
      "Orders": l.orderAmount || 0,
      "Online": l.onlineCollected || 0,
      "Cash": l.cashCollected || 0,
      "Closing": l.closingBalance || 0,
      "Status": l.status || "",
      "Day Close": l.dayClose ? "YES" : "NO",
      "Remark": l.rejectionRemark || ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SalesmanLedgers");
    XLSX.writeFile(workbook, `SalesmanLedger_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2>Salesman Ledger Management</h2>
          <button
            onClick={handleExportExcel}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              padding: "8px 16px",
              border: "none",
              borderRadius: 4,
              cursor: "pointer"
            }}
          >
            Export to Excel
          </button>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <input
            type="text"
            placeholder="Search by Party ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: 8, width: 250, borderRadius: 4, border: "1px solid #ccc" }}
          />
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{ padding: 8, borderRadius: 4, border: "1px solid #ccc" }}
          />
          <button onClick={loadLedgers} style={{ padding: "8px 16px", cursor: "pointer" }}>Reload</button>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div style={{ overflowX: "auto", maxHeight: "70vh" }}>
            <table border="1" cellPadding="8" style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0 }}>
                <tr>
                  <th>Date</th>
                  <th>Party ID</th>
                  <th>Mode</th>
                  <th style={{ backgroundColor: "#f8f9fa" }}>Opening Balance</th>
                  <th style={{ backgroundColor: "#fff3cd" }}>Orders</th>
                  <th style={{ backgroundColor: "#e3f2fd" }}>CDM</th>
                  <th style={{ backgroundColor: "#e0f7fa" }}>Niche Brands</th>
                  <th style={{ backgroundColor: "#fce4ec" }}>Online Collection</th>
                  <th style={{ backgroundColor: "#e8f5e9" }}>Cash Collected</th>
                  <th style={{ backgroundColor: "#f8f9fa" }}>Closing Balance</th>
                  <th>Status</th>
                  <th>Day Close</th>
                  <th>Remark</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id}>
                    <td>{l.dateIST}</td>
                    <td>{l.partyId}</td>
                    <td style={{ fontWeight: "500", color: "#666" }}>{l.payment_mode}</td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        value={Number(l.openingBalance || 0).toFixed(2)}
                        onChange={(e) => updateFieldLocal(l.id, "openingBalance", e.target.value)}
                        style={{ width: 80, padding: "2px 5px", border: "1px solid #ddd", borderRadius: 4 }}
                      />
                    </td>
                    <td>{Number(l.orderAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style={{ color: "#007bff", fontWeight: "bold" }}>
                      {Number(l.cdmAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ color: "#17a2b8", fontWeight: "bold" }}>
                      {Number(l.nicheBrandAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td>{Number(l.onlineCollected || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>{Number(l.cashCollected || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style={{ fontWeight: "bold", color: "#f74605" }}>
                      ₹{(
                        Number(l.openingBalance || 0) +
                        Number(l.orderAmount || 0) +
                        Number(l.cdmAmount || 0) +
                        Number(l.nicheBrandAmount || 0) -
                        Number(l.onlineCollected || 0) - 
                        Number(l.cashCollected || 0)
                      ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td>
                      <select
                        value={l.status || "PENDING"}
                        onChange={(e) => updateFieldLocal(l.id, "status", e.target.value)}
                      >
                        <option value="PENDING">PENDING</option>
                        <option value="APPROVED">APPROVED</option>
                        <option value="REJECTED">REJECTED</option>
                      </select>
                    </td>
                    <td>
                       <input
                        type="checkbox"
                        checked={!!l.dayClose}
                        onChange={(e) => updateFieldLocal(l.id, "dayClose", e.target.checked)}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={l.rejectionRemark || ""}
                        onChange={(e) => updateFieldLocal(l.id, "rejectionRemark", e.target.value)}
                        placeholder="Remark"
                      />
                    </td>
                    <td>
                      <button
                        onClick={() => saveLedgerChanges(l)}
                        style={{
                          backgroundColor: "#007bff",
                          color: "white",
                          padding: "4px 12px",
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                      >
                        Save
                      </button>
                    </td>
                  </tr>
                ))}
                  {filtered.length === 0 && (
                  <tr>
                    <td colSpan="14" style={{ textAlign: "center", padding: 20 }}>No records found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default SalesmanLedgerManager;
